import { z } from 'zod/v4';
import { FacilityProcessTranslationsSchema } from './admin-facility-process.js';

export const AdminFacilityProcessesListSortColumns = [
  'name',
  'productionProcessId',
  'updatedAt',
] as const;

export type AdminFacilityProcessesListSortColumn =
  (typeof AdminFacilityProcessesListSortColumns)[number];

const sortColumns = AdminFacilityProcessesListSortColumns.join('|');

// One or more comma-separated `<column>:<ASC|DESC>` terms, e.g.
// `name:DESC,updatedAt:ASC` — the same grammar the legacy route accepted.
const sortPattern = new RegExp(
  `^(${sortColumns}):(ASC|DESC)(,(${sortColumns}):(ASC|DESC))*$`,
);

// Querystrings arrive as strings; accept a real boolean too.
const booleanLike = z
  .union([z.boolean(), z.string()])
  .optional()
  .transform(v => v === true || v === 'true');

export const AdminFacilityProcessesListQuerySchema = z.object({
  page: z.coerce.number().int().min(0).default(0),
  count: z.coerce.number().int().min(0).max(500).default(15),
  sort: z.string().regex(sortPattern).optional(),
  // JSON object; `name` and `productionProcessId` are honoured, each as a
  // case-insensitive substring match.
  filter: z.string().max(512).optional(),
  // Every matching row in one page, ignoring `page` / `count`.
  isDropDown: booleanLike,
});

export type AdminFacilityProcessesListQuery = z.infer<
  typeof AdminFacilityProcessesListQuerySchema
>;

export const AdminFacilityProcessRowSchema = z.object({
  id: z.string(),
  name: z.string(),
  productionProcessId: z.string().nullable(),
  // As stored — no `en` fallback here, unlike the single-record endpoint.
  translations: FacilityProcessTranslationsSchema.nullable(),
  updatedAt: z.coerce.date().nullable(),
  updatedByUserName: z.string().nullable(),
});

export type AdminFacilityProcessRow = z.infer<
  typeof AdminFacilityProcessRowSchema
>;

/** The `data` payload; the route wraps it with the paginated `metadata` block. */
export const AdminFacilityProcessesListResponseSchema = z.array(
  AdminFacilityProcessRowSchema,
);

export type AdminFacilityProcessesListResponse = z.infer<
  typeof AdminFacilityProcessesListResponseSchema
>;
